
import { SaleDetails } from "@/types/sales";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatCurrency } from "@/utils/format";
import { formatSalesDate } from "@/utils/dateFormat";
import { getLeadSourceIcon } from "@/utils/sales/icons/leadSourceIcons";
import { calculateTotal } from "@/utils/sales/groupSales";

interface SalesByLeadSourceProps {
  salesMetrics: {
    monthlyPolicies: SaleDetails[];
  } | undefined;
}

import { ProducerNameDisplay } from "./components/ProducerNameDisplay";

export const SalesByLeadSource = ({ salesMetrics }: SalesByLeadSourceProps) => {
  const groupedSales = salesMetrics?.monthlyPolicies?.reduce((acc, policy) => {
    const source = policy.lead_source || 'Unknown';
    if (!acc[source]) {
      acc[source] = [];
    }
    acc[source].push(policy);
    return acc;
  }, {} as Record<string, SaleDetails[]>);

  return (
    <Card className="bg-gradient-to-br from-white via-[#F7F5FF] to-[#E5DEFF]/80 hover:shadow-lg border-purple-100">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-lg font-medium">
          Sales by Lead Source
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-8">
          {groupedSales && Object.entries(groupedSales).map(([source, sales]) => {
            const { icon: Icon } = getLeadSourceIcon(source);
            return (
              <div key={source} className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    {source}
                  </h3>
                  <div className="text-sm text-muted-foreground">
                    Total: {formatCurrency(calculateTotal(sales))}
                  </div>
                </div>
                <div className="relative w-full overflow-auto">
                  <table className="w-full caption-bottom text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="h-12 px-4 text-left align-middle font-medium">Date</th>
                        <th className="h-12 px-4 text-left align-middle font-medium">Customer</th>
                        <th className="h-12 px-4 text-left align-middle font-medium">Producer</th>
                        <th className="h-12 px-4 text-left align-middle font-medium">Insurance Type</th>
                        <th className="h-12 px-4 text-right align-middle font-medium">Premium</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sales
                        .sort((a, b) => new Date(b.sales_date).getTime() - new Date(a.sales_date).getTime())
                        .map((sale, index) => (
                          <tr key={index} className="border-b">
                            <td className="p-4 align-middle">{formatSalesDate(sale.sales_date)}</td>
                            <td className="p-4 align-middle">
                              {sale.customer_first_name} {sale.customer_last_name}
                            </td>
                            <td className="p-4 align-middle">
                              <ProducerNameDisplay producerName={sale.producer_name} />
                            </td>
                            <td className="p-4 align-middle">
                              {sale.insurance_type}
                            </td>
                            <td className="p-4 align-middle text-right">
                              {formatCurrency(sale.premium_amount)}
                            </td>
                          </tr>
                        ))}
                      {sales.length > 1 && (
                        <tr className="border-t border-t-2">
                          <td colSpan={4} className="p-4 align-middle font-medium text-right">
                            Subtotal:
                          </td>
                          <td className="p-4 align-middle text-right font-medium">
                            {formatCurrency(calculateTotal(sales))}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
